import {useMutation, type UseMutationResult} from '@tanstack/react-query'
import {type AxiosResponse} from 'axios'

import Logger from 'services/Logger'
import {PoliciesService} from 'services/api'
import {
  type ApplicationBypassMutation,
  BypassDataServerRequest,
  type BypassUpdateResource,
  type CustomBypassApiItem,
  type GenericMessageResponse,
  type UrlBypassMutation,
} from 'interfaces'
import {useFeatureFlag} from 'hooks/useFeatureFlag'

export const UPDATE_BYPASS_QUERY_KEY = 'updateBypassData'

interface BaseUpdateBypassQueryProps {
  onSuccess: () => void
  onError?: () => void
}

interface UpdateBypassQueryProps extends BaseUpdateBypassQueryProps {
  resource?: BypassUpdateResource
}

interface LegacyUpdateBypassParams {
  policyName: string
  payload: BypassDataServerRequest
  applicationMutation?: never
  urlMutation?: never
}

interface UrlUpdateBypassParams {
  policyName: string
  payload: BypassDataServerRequest
  applicationMutation?: never
  urlMutation: UrlBypassMutation
}

interface ApplicationUpdateBypassParams {
  policyName: string
  payload: BypassDataServerRequest
  applicationMutation: ApplicationBypassMutation
  urlMutation?: never
}

type UpdateBypassParams =
  | LegacyUpdateBypassParams
  | UrlUpdateBypassParams
  | ApplicationUpdateBypassParams

type UpdateBypassResponse = Promise<AxiosResponse<GenericMessageResponse>>

const updateUrlBypassResource = async (
  policyName: string,
  payload: BypassDataServerRequest,
  urlMutation: UrlBypassMutation,
): UpdateBypassResponse => {
  switch (urlMutation.type) {
    case 'reset-to-base':
      return PoliciesService.updateUrlBypass(policyName, {
        data: {inheritsFromBase: true},
      })

    case 'delete-custom':
      if (urlMutation.isInherited) {
        return PoliciesService.updateUrlBypass(policyName, {
          data: {custom: getUrlCustomBypassItemsFromPayload(payload)},
        })
      }

      return PoliciesService.deleteUrlBypass(policyName, {
        data: {custom: {urls: urlMutation.urls}},
      })

    case 'update-default':
      return PoliciesService.updateUrlBypass(policyName, {
        data: {
          custom: getUrlCustomBypassItemsFromPayload(payload),
          default: urlMutation.default,
        },
      })

    case 'upsert-custom': {
      const response = await PoliciesService.updateUrlBypass(policyName, {
        data: {custom: getUrlCustomBypassItemsFromPayload(payload)},
      })

      if (urlMutation.deleteCustomUrls?.length) {
        await PoliciesService.deleteUrlBypass(policyName, {
          data: {custom: {urls: urlMutation.deleteCustomUrls}},
        })
      }

      return response
    }
  }
}

const updateApplicationBypassResource = (
  policyName: string,
  applicationMutation: ApplicationBypassMutation,
): UpdateBypassResponse => {
  switch (applicationMutation.type) {
    case 'reset-to-base':
      return PoliciesService.updateApplicationBypass(policyName, {
        data: {inheritsFromBase: true},
      })

    case 'update-default':
      return PoliciesService.updateApplicationBypass(policyName, {
        data: {
          custom: applicationMutation.custom,
          default: applicationMutation.default,
        },
      })

    case 'upsert-custom':
      return PoliciesService.updateApplicationBypass(policyName, {
        data: {custom: applicationMutation.custom},
      })
  }
}

const toError = (error: unknown): Error =>
  error instanceof Error ? error : new Error(String(error))

const getUrlCustomBypassItemsFromPayload = (
  payload: BypassDataServerRequest,
): CustomBypassApiItem[] =>
  // The new URL API persists only the custom entries it receives when an
  // inherited policy becomes customized, so use the full effective client list.
  payload.customDomainBypassList.map(({name, note}) => ({
    name,
    note,
  }))

export function useUpdateBypass({
  onSuccess,
  onError,
  resource = 'legacy',
}: UpdateBypassQueryProps): UseMutationResult<
  AxiosResponse<GenericMessageResponse>,
  unknown,
  UpdateBypassParams
> {
  const {isBypassApiMigrationEnabled} = useFeatureFlag()

  return useMutation(
    async ({
      policyName,
      payload,
      applicationMutation,
      urlMutation,
    }: UpdateBypassParams) => {
      if (isBypassApiMigrationEnabled && resource === 'url') {
        if (!urlMutation) {
          throw new Error(
            'URL bypass mutation metadata is required for URL bypass API updates.',
          )
        }

        return updateUrlBypassResource(policyName, payload, urlMutation)
      }

      if (isBypassApiMigrationEnabled && resource === 'application') {
        if (!applicationMutation) {
          throw new Error(
            'Application bypass mutation metadata is required for Application bypass API updates.',
          )
        }

        return updateApplicationBypassResource(policyName, applicationMutation)
      }

      return PoliciesService.updateBypass({
        policyName,
        data: payload,
      })
    },
    {
      onSuccess: onSuccess,
      onError: (error: unknown) => {
        Logger.LogError(toError(error))
        onError?.()
      },
    },
  )
}
